import type { SelectedTheme } from "@revolt/state/stores/Theme";

/**
 * Zarzour default palette ("soft indigo").
 *
 * Every colour is derived from one hue, the logo indigo #4F5DFF (OKLCH hue
 * 272.4), in even OKLCH lightness steps, so surfaces, text, accent and states
 * read as one family:
 *   surfaces  L 0.17 → 0.29, chroma ≈ 0.02–0.03 (calm tinted slate)
 *   text      L 0.955 / 0.80 / 0.64, chroma ≤ 0.03
 *   accent    L 0.555, chroma 0.16 (the logo indigo, softened to sit with the
 *             panels; white text on it ≥ 4.5:1)
 *   states    live / danger / warning at matching lightness and chroma
 * Text contrast on chat: 14.7 / 8.9 / 4.9 : 1.
 */
export const ZARZOUR_UNIFIED_DARK = {
  bgApp: "#0C0F19",
  bgRail: "#10131F",
  bgSidebar: "#151926",
  bgChat: "#1A1D2B",
  bgRaised: "#262A3B",
  bgInput: "#121521",
  fgPrimary: "#EEF0F6",
  fgSecondary: "#B9BDCC",
  fgMuted: "#868B9F",
  accentDefault: "#5568CF",
  // accent used as text (links, mentions) — same hue, lifted for legibility
  accentText: "#A1B4F8",
  // selected rows and soft accent tiles
  accentSoft: "#262E4F",
  // the one colour for "happening now": voice, speaking, online
  live: "#55D7BE",
  // default avatar tint, from the same family
  avatar: "#5F96D4",
  error: "#F17070",
  onError: "#1A0A0C",
  errorContainer: "#3F1A20",
  onErrorContainer: "#FFDAD9",
  success: "#55D7BE",
  warning: "#F1C45E",
} as const;

/**
 * Earlier defaults (the Discord-like "Signature Dark", then a short-lived
 * near-black "Starling night"). Stored themes still carrying one of these
 * exact sets are moved to the current palette; customised themes are kept.
 */
export const RETIRED_DEFAULTS = [
  {
    accents: ["#7C8FE8"],
    surfaces: {
      bgApp: "#101217",
      bgRail: "#151820",
      bgSidebar: "#1B1F28",
      bgChat: "#20242E",
      bgRaised: "#292E39",
    },
  },
  {
    accents: ["#4F5DFF", "#5D6CFF"],
    surfaces: {
      bgApp: "#070B16",
      bgRail: "#090E1B",
      bgSidebar: "#0C1222",
      bgChat: "#0E1528",
      bgRaised: "#18213D",
    },
  },
] as const;

/** Desktop panel targets from Signature Dark reference */
export const ZARZOUR_LAYOUT = {
  railWidth: 72,
  sidebarWidth: 260,
  memberWidth: 240,
  headerHeight: 56,
  accountBarHeight: 60,
  composerMinHeight: 56,
} as const;

export const ZARZOUR_SPACING = [4, 8, 12, 16, 20, 24, 32, 40, 48, 64] as const;

export type ZarzourIconStyle = "outline" | "filled" | "expressive";

/**
 * Relative luminance helpers for contrast-aware on-accent text.
 */
function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  const full =
    h.length === 3
      ? h
          .split("")
          .map((c) => c + c)
          .join("")
      : h.slice(0, 6);
  const n = parseInt(full, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function luminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function contrastOn(accent: string): string {
  const ink = ZARZOUR_UNIFIED_DARK.bgApp;
  const paper = "#FFFFFF";
  const value = luminance(accent);
  const lightContrast = (luminance(paper) + 0.05) / (value + 0.05);
  // white on saturated accents (the brand indigo) whenever it is legible as
  // normal text (WCAG AA); dark ink only on pale accents
  if (lightContrast >= 4.5) return paper;
  const darkContrast = (value + 0.05) / (luminance(ink) + 0.05);
  return darkContrast >= lightContrast ? ink : paper;
}

function mix(hex: string, toward: string, amount: number): string {
  const a = hexToRgb(hex);
  const b = hexToRgb(toward);
  const m = (i: number) =>
    Math.round(a[i] + (b[i] - a[i]) * Math.min(1, Math.max(0, amount)));
  return `#${[m(0), m(1), m(2)]
    .map((v) => v.toString(16).padStart(2, "0"))
    .join("")}`;
}

export type ZarzourSurfaces = {
  bgApp?: string;
  bgRail?: string;
  bgSidebar?: string;
  bgChat?: string;
  bgRaised?: string;
};

/**
 * Material-compatible colour map using Unified Dark surfaces.
 * Keeps `--md-sys-color-*` consumers working while leaving Material You.
 */
export function generateZarzourColourScheme(
  accent: string,
  darkMode: boolean,
  surfaces?: ZarzourSurfaces,
): Record<string, string> {
  const primary = accent || ZARZOUR_UNIFIED_DARK.accentDefault;
  const onPrimary = contrastOn(primary);

  if (!darkMode) {
    // Soft light companion — same family, not Material You pastel.
    const bg = "#F4F6FA";
    const rail = "#E8ECF4";
    const sidebar = "#EFF2F8";
    const chat = "#FFFFFF";
    const raised = "#FFFFFF";
    const fg = "#151820";
    const fgVar = "#4A5568";
    const muted = "#657086";
    return {
      primary,
      "on-primary": onPrimary,
      "primary-container": mix(primary, "#FFFFFF", 0.75),
      "on-primary-container": mix(primary, "#000000", 0.55),
      secondary: mix(primary, muted, 0.35),
      "on-secondary": onPrimary,
      "secondary-container": mix(primary, "#FFFFFF", 0.85),
      "on-secondary-container": fg,
      tertiary: "#167847",
      "on-tertiary": "#FFFFFF",
      "tertiary-container": mix(ZARZOUR_UNIFIED_DARK.success, "#FFFFFF", 0.8),
      "on-tertiary-container": mix(
        ZARZOUR_UNIFIED_DARK.success,
        "#000000",
        0.5,
      ),
      error: "#B4233B",
      "on-error": "#FFFFFF",
      "error-container": mix(ZARZOUR_UNIFIED_DARK.error, "#FFFFFF", 0.85),
      "on-error-container": mix(ZARZOUR_UNIFIED_DARK.error, "#000000", 0.45),
      "primary-fixed": mix(primary, "#FFFFFF", 0.7),
      "primary-fixed-dim": mix(primary, "#FFFFFF", 0.55),
      "on-primary-fixed": fg,
      "on-primary-fixed-variant": fgVar,
      "secondary-fixed": mix(primary, "#FFFFFF", 0.8),
      "secondary-fixed-dim": mix(primary, "#FFFFFF", 0.65),
      "on-secondary-fixed": fg,
      "on-secondary-fixed-variant": fgVar,
      "tertiary-fixed": mix(ZARZOUR_UNIFIED_DARK.success, "#FFFFFF", 0.8),
      "tertiary-fixed-dim": mix(ZARZOUR_UNIFIED_DARK.success, "#FFFFFF", 0.65),
      "on-tertiary-fixed": fg,
      "on-tertiary-fixed-variant": fgVar,
      "surface-dim": rail,
      surface: bg,
      "surface-bright": chat,
      "surface-container-lowest": chat,
      "surface-container-low": sidebar,
      "surface-container": raised,
      "surface-container-high": rail,
      "surface-container-highest": mix(rail, muted, 0.15),
      "on-surface": fg,
      "on-surface-variant": fgVar,
      outline: muted,
      "outline-variant": mix(muted, "#FFFFFF", 0.45),
      "inverse-surface": ZARZOUR_UNIFIED_DARK.bgChat,
      "inverse-on-surface": ZARZOUR_UNIFIED_DARK.fgPrimary,
      "inverse-primary": primary,
      scrim: "#000000",
      shadow: "#000000",
    };
  }

  const d = ZARZOUR_UNIFIED_DARK;
  const bgApp = surfaces?.bgApp ?? d.bgApp;
  const bgRail = surfaces?.bgRail ?? d.bgRail;
  const bgSidebar = surfaces?.bgSidebar ?? d.bgSidebar;
  const bgChat = surfaces?.bgChat ?? d.bgChat;
  const bgRaised = surfaces?.bgRaised ?? d.bgRaised;

  return {
    primary,
    "on-primary": onPrimary,
    "primary-container": mix(primary, bgRaised, 0.55),
    "on-primary-container": d.fgPrimary,
    secondary: mix(primary, d.fgMuted, 0.4),
    "on-secondary": d.fgPrimary,
    "secondary-container": mix(bgRaised, primary, 0.18),
    "on-secondary-container": d.fgSecondary,
    tertiary: d.success,
    "on-tertiary": "#0A1A12",
    "tertiary-container": mix(d.success, bgRaised, 0.65),
    "on-tertiary-container": d.fgPrimary,
    error: d.error,
    "on-error": d.onError,
    "error-container": d.errorContainer,
    "on-error-container": d.onErrorContainer,
    "primary-fixed": mix(primary, bgChat, 0.35),
    "primary-fixed-dim": mix(primary, bgChat, 0.5),
    "on-primary-fixed": d.fgPrimary,
    "on-primary-fixed-variant": d.fgSecondary,
    "secondary-fixed": mix(primary, bgRaised, 0.45),
    "secondary-fixed-dim": mix(primary, bgRaised, 0.6),
    "on-secondary-fixed": d.fgPrimary,
    "on-secondary-fixed-variant": d.fgSecondary,
    "tertiary-fixed": mix(d.success, bgRaised, 0.45),
    "tertiary-fixed-dim": mix(d.success, bgRaised, 0.6),
    "on-tertiary-fixed": d.fgPrimary,
    "on-tertiary-fixed-variant": d.fgSecondary,
    "surface-dim": bgApp,
    surface: bgApp,
    "surface-bright": bgRaised,
    "surface-container-lowest": bgApp,
    "surface-container-low": bgSidebar,
    "surface-container": bgChat,
    "surface-container-high": bgRaised,
    "surface-container-highest": bgRail,
    "on-surface": d.fgPrimary,
    "on-surface-variant": d.fgSecondary,
    outline: d.fgMuted,
    "outline-variant": mix(d.fgMuted, bgChat, 0.55),
    "inverse-surface": "#EEF1F7",
    "inverse-on-surface": bgApp,
    "inverse-primary": primary,
    scrim: "#000000",
    shadow: "#000000",
  };
}

/**
 * Semantic `--z-*` tokens for new UI (and Appearance Studio).
 */
export function createZarzourSemanticVariables(
  theme: SelectedTheme & {
    iconAccent?: string;
    iconStyle?: ZarzourIconStyle;
    iconSize?: number;
    iconFill?: number;
    iconGlow?: number;
    radius?: number;
    railWidth?: number;
    sidebarWidth?: number;
    density?: "compact" | "comfortable" | "spacious";
    surfaces?: ZarzourSurfaces;
  },
): Record<string, string> {
  const d = ZARZOUR_UNIFIED_DARK;
  const accent = theme.accent || d.accentDefault;
  const iconAccent = theme.iconAccent || accent;
  const dark = theme.darkMode;
  const surfaces = theme.surfaces;

  const densityPad =
    theme.density === "compact"
      ? "4px"
      : theme.density === "spacious"
        ? "12px"
        : "8px";

  // density scales real measurements: nav/menu rows and message group spacing
  // (relative to the user's own spacing setting, which stays the baseline)
  const rowHeight =
    theme.density === "compact"
      ? "30px"
      : theme.density === "spacious"
        ? "40px"
        : "34px";
  const baseGroupSpacing = theme.messageGroupSpacing ?? 12;
  const groupSpacing =
    theme.density === "compact"
      ? Math.max(2, baseGroupSpacing - 6)
      : theme.density === "spacious"
        ? baseGroupSpacing + 8
        : baseGroupSpacing;

  return {
    "--z-bg-app": dark ? (surfaces?.bgApp ?? d.bgApp) : "#F4F6FA",
    "--z-bg-rail": dark ? (surfaces?.bgRail ?? d.bgRail) : "#E8ECF4",
    "--z-bg-sidebar": dark ? (surfaces?.bgSidebar ?? d.bgSidebar) : "#EFF2F8",
    "--z-bg-chat": dark ? (surfaces?.bgChat ?? d.bgChat) : "#FFFFFF",
    /*
     * One surface for the whole app shell (server rail, channel list,
     * headers, chat, member list), separated by hairlines rather than by
     * different shades. Floating pieces (account panel, composer) sit on
     * --z-bg-raised.
     */
    "--z-bg-shell": "var(--z-bg-chat)",
    // the frame around the panels: top bar + server rail, one step darker
    "--z-bg-frame": dark ? (surfaces?.bgApp ?? d.bgApp) : "#E8ECF4",
    "--z-titlebar-height": "32px",
    // account panel + composer: one step off the shell in both modes
    // (in light mode raised and chat are both white)
    "--z-bg-dock": dark ? "var(--z-bg-raised)" : "#EDF0F5",
    "--z-bg-raised": dark ? (surfaces?.bgRaised ?? d.bgRaised) : "#FFFFFF",
    "--z-fg-primary": dark ? d.fgPrimary : "#151820",
    "--z-fg-secondary": dark ? d.fgSecondary : "#4A5568",
    "--z-fg-muted": dark ? d.fgMuted : "#657086",
    "--z-accent": accent,
    // hairlines and hovers carry a little of the indigo so the navy reads as
    // one tinted family rather than neutral grey
    "--z-border-subtle": dark
      ? "rgba(185,189,204,0.08)"
      : "rgba(21,24,32,0.09)",
    "--z-border-strong": dark
      ? "rgba(185,189,204,0.15)"
      : "rgba(21,24,32,0.17)",
    "--z-nav-hover": dark ? "rgba(185,189,204,0.055)" : "rgba(21,24,32,0.045)",
    "--z-nav-selected": dark
      ? accent.toLowerCase() === d.accentDefault.toLowerCase()
        ? d.accentSoft
        : `color-mix(in srgb, ${accent} 22%, ${surfaces?.bgSidebar ?? d.bgSidebar})`
      : "rgba(21,24,32,0.075)",
    "--z-nav-icon": "var(--z-icon-muted)",
    /*
     * Semantic icon roles. Every role derives from --z-icon-accent, so the
     * Appearance Studio icon accent reaches every icon, but only "active" uses it
     * at full strength — resting icons stay subdued, danger stays red.
     */
    "--z-icon-muted":
      "color-mix(in srgb, var(--z-fg-muted) 78%, var(--z-icon-accent))",
    "--z-icon-neutral":
      "color-mix(in srgb, var(--z-fg-secondary) 82%, var(--z-icon-accent))",
    "--z-icon-hover":
      "color-mix(in srgb, var(--z-fg-primary) 80%, var(--z-icon-accent))",
    "--z-icon-active": "var(--z-icon-accent)",
    "--z-icon-danger": "var(--z-danger)",
    "--z-icon-accent": iconAccent,
    "--z-icon-style": theme.iconStyle ?? "expressive",
    "--z-icon-size": `${theme.iconSize ?? 20}px`,
    "--z-icon-fill": String(theme.iconFill ?? 0.22),
    "--z-icon-glow": String(theme.iconGlow ?? 0),
    "--z-radius": `${theme.radius ?? 10}px`,
    "--z-rail-width": `${theme.railWidth ?? 72}px`,
    "--z-sidebar-width": `${theme.sidebarWidth ?? 260}px`,
    "--z-member-width": "240px",
    "--z-header-height": "56px",
    "--z-account-bar-height": "60px",
    "--z-composer-min-height": "56px",
    /*
     * Bottom "dock": the floating account panel and the message composer
     * share one height and one distance from the window edge, so their top
     * and bottom edges line up across the window
     */
    "--z-dock-height": "56px",
    "--z-dock-gap": "8px",
    /* Overlay surfaces: one step above raised so menus read over cards */
    // menus/popovers sit one step DARKER than chat with a crisp border,
    // so they read as floating desktop surfaces rather than lighter cards
    "--z-bg-popover": dark
      ? `color-mix(in srgb, ${surfaces?.bgApp ?? d.bgApp} 55%, ${surfaces?.bgSidebar ?? d.bgSidebar})`
      : "#FFFFFF",
    "--z-bg-modal": dark ? (surfaces?.bgChat ?? d.bgChat) : "#FFFFFF",
    "--z-bg-input": dark ? d.bgInput : "#E8ECF4",
    "--z-fg-disabled": dark ? "rgba(238,240,246,0.32)" : "rgba(21,24,32,0.35)",
    "--z-accent-hover": mix(accent, dark ? "#FFFFFF" : "#000000", 0.1),
    "--z-accent-pressed": mix(accent, "#000000", 0.15),
    "--z-accent-muted": `color-mix(in srgb, ${accent} 16%, transparent)`,
    // accent used AS text (links, highlights): lifted so it stays legible on
    // navy; fills and buttons keep the full brand accent
    "--z-accent-text": dark
      ? accent.toLowerCase() === d.accentDefault.toLowerCase()
        ? d.accentText
        : mix(accent, "#FFFFFF", 0.45)
      : accent,
    "--z-on-accent": contrastOn(accent),
    "--z-danger": dark ? d.error : "#B4233B",
    "--z-danger-muted": dark ? "rgba(241,112,112,0.14)" : "rgba(180,35,59,0.1)",
    /*
     * Starling teal: the one colour for "happening now" (voice connected,
     * someone speaking, online). Never used for static UI.
     */
    "--z-live": dark ? d.live : "#0F9F8F",
    "--z-live-muted": `color-mix(in srgb, ${dark ? d.live : "#0F9F8F"} 16%, transparent)`,
    "--z-presence-online": "var(--z-live)",
    "--z-presence-idle": d.warning,
    "--z-presence-dnd": d.error,
    "--z-presence-focus": d.accentDefault,
    "--z-presence-offline": d.fgMuted,
    "--z-mention-bg": dark ? "rgba(250,204,21,0.07)" : "rgba(250,204,21,0.14)",
    "--z-mention-bar": d.warning,
    "--z-unread": dark ? d.fgPrimary : "#151820",
    "--z-badge": dark ? "#EF4444" : "#B4233B",
    "--z-focus": mix(accent, "#FFFFFF", 0.35),
    "--z-status-success": dark ? d.success : "#167847",
    "--z-status-warning": dark ? d.warning : "#926400",
    "--z-status-error": dark ? d.error : "#B4233B",
    "--z-density-pad": densityPad,
    "--z-row-height": rowHeight,
    "--message-group-spacing": `${groupSpacing}px`,
    /* Restrained Signature Dark atmosphere — accent wash, not a full-scene gradient */
    "--z-atmosphere": dark
      ? `radial-gradient(1200px 600px at 70% 0%, color-mix(in srgb, ${accent} 9%, transparent), transparent 60%)`
      : "none",
    "--zarzour-bg-app": dark ? (surfaces?.bgApp ?? d.bgApp) : "#F4F6FA",
    "--zarzour-bg-rail": dark ? (surfaces?.bgRail ?? d.bgRail) : "#E8ECF4",
    "--zarzour-bg-sidebar": dark
      ? (surfaces?.bgSidebar ?? d.bgSidebar)
      : "#EFF2F8",
    "--zarzour-bg-chat": dark ? (surfaces?.bgChat ?? d.bgChat) : "#FFFFFF",
    "--zarzour-bg-raised": dark
      ? (surfaces?.bgRaised ?? d.bgRaised)
      : "#FFFFFF",
    "--zarzour-text-primary": dark ? d.fgPrimary : "#151820",
    "--zarzour-text-secondary": dark ? d.fgSecondary : "#4A5568",
    "--zarzour-text-muted": d.fgMuted,
    "--zarzour-accent": accent,
    "--z-space-1": "4px",
    "--z-space-2": "8px",
    "--z-space-3": "12px",
    "--z-space-4": "16px",
    "--z-space-5": "20px",
    "--z-space-6": "24px",
    "--z-space-7": "32px",
    "--z-space-8": "40px",
    "--z-space-9": "48px",
    "--z-space-10": "64px",
    "--z-focus-ring": `0 0 0 2px ${mix(accent, "#FFFFFF", 0.35)}`,
    "--z-shadow-sm": dark
      ? "0 1px 2px rgba(0,0,0,0.35)"
      : "0 1px 2px rgba(16,18,23,0.08)",
    "--z-shadow-md": dark
      ? "0 4px 16px rgba(0,0,0,0.4)"
      : "0 4px 16px rgba(16,18,23,0.1)",
  };
}
